import React, { useState, useEffect } from 'react';
import { useToast } from '@/components/ui/use-toast';
import PaginationComponent from '@/components/common/PaginationComponent';
import { FullScreenLoader, TableBlurLoader } from '@/components/ui/loader';
import rakeMovementService from '@/services/rakeMovementService';
import tripService from '@/services/tripService';
import { useAuth } from '@/contexts/AuthContext';
import { CommonSearchInput } from '@/components/ui/common-search-input';
import CreateSecondaryDispatchDialog from './secondaryDispatch/CreateSecondaryDispatchDialog';
import RakeReceiptsTable from './components/RakeReceiptsTable';
import LogGateInDialog from './components/LogGateInDialog';

const RakeReceiptsPage = () => {
  const { userProfile, userRole } = useAuth();
  const { toast } = useToast();

  const role = (userProfile?.role || userRole || '').toLowerCase();
  const isReadOnly = role === 'ipl_super_admin' || role === 'ipl_admin';
  const canDispatch = ['region_head', 'account_head', 'field_officer'].includes(role) || role === 'ipl_super_admin' || role === 'ipl_admin';

  const [activeTab, setActiveTab] = useState('pending');
  const [trips, setTrips] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const pageSize = 10;

  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedTrip, setSelectedTrip] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const [secondaryDispatchOpen, setSecondaryDispatchOpen] = useState(false);
  const [dispatchParentTripId, setDispatchParentTripId] = useState(null);

  useEffect(() => {
    fetchRakeReceipts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, activeTab, searchQuery]);

  const fetchRakeReceipts = async () => {
    try {
      setLoading(true);
      const skip = (currentPage - 1) * pageSize;

      let rrNumber = '';
      let tripNumber = '';
      if (searchQuery.trim()) {
        if (searchQuery.toUpperCase().startsWith('RR')) {
          rrNumber = searchQuery.trim();
        } else {
          tripNumber = searchQuery.trim();
        }
      }

      const response = await rakeMovementService.getMyRakeMovements({
        skip,
        limit: pageSize,
        rr_number: rrNumber,
        trip_number: tripNumber
      });

      const rawList = Array.isArray(response) ? response : response?.data || [];

      const filteredRawList = rawList.filter(movement => {
        if (activeTab === 'pending') {
          return !movement.is_received;
        } else {
          return movement.is_received;
        }
      });

      const transformedTrips = filteredRawList.map(movement => {
        const baseline = movement.baseline_qty_mt || movement.total_dispatched_qty_mt || 0;
        const receivedQty = movement.receipt_qty !== null && movement.receipt_qty !== undefined ? parseFloat(movement.receipt_qty) : null;
        let varianceQty = null;
        let varianceType = 'EXACT';

        if (receivedQty !== null) {
          varianceQty = receivedQty - baseline;
          if (varianceQty > 0.001) {
            varianceType = 'SURPLUS';
          } else if (varianceQty < -0.001) {
            varianceType = 'SHORTAGE';
          }
        }

        const consigneeState = movement.state?.name || movement.consignee_state || 'N/A';

        return {
          id: movement.id,
          trip_number: movement.trip_number,
          rr_number: movement.rr_number,
          rr_date: movement.rr_date,
          scheduled_end_time: movement.scheduled_end_time,
          rake_point_name: movement.rake_point?.name || movement.rake_point_name || 'N/A',
          product_name: movement.items?.[0]?.product_type?.name || movement.product_name || 'N/A',
          transporter_name: movement.transporter_name || movement.transporter?.company_name || 'N/A',
          baseline_qty_mt: baseline,
          dispatched_qty_mt: movement.total_dispatched_qty_mt,
          receipt_qty_mt: receivedQty,
          missing_wagons: movement.missing_wagons,
          variance_qty_mt: Math.abs(varianceQty),
          variance_type: varianceType,
          receipt_status: movement.is_received ? 'Received' : 'Pending',
          consignee_state: consigneeState,
          received_at: movement.received_at,
          received_by: movement.received_by_username || movement.received_by,
          items: movement.items,
          can_create_secondary: movement.is_received && movement.receipt_qty > 0
        };
      });

      const count = (response && typeof response === 'object' && !Array.isArray(response) && (response.total || response.count))
        ? (response.total || response.count)
        : (filteredRawList.length < pageSize ? skip + filteredRawList.length : skip + filteredRawList.length + 1);

      setTrips(transformedTrips);
      setTotalItems(count);
      setError(null);
    } catch (err) {
      console.error('Failed to fetch rake receipts:', err);
      setError('Failed to load rake receipts');
      toast({
        title: 'Error',
        description: err?.message || 'Failed to load rake receipts. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
      setIsInitialLoad(false);
    }
  };

  const handleOpenGateIn = (trip) => {
    setSelectedTrip(trip);
    setDialogOpen(true);
  };

  const handleOpenCreateDispatch = (trip) => {
    setDispatchParentTripId(trip.id);
    setSecondaryDispatchOpen(true);
  };

  const handleSubmitReceipt = async (receiptData) => {
    try {
      setSubmitting(true);
      const payload = {
        receipt_qty: receiptData.receipt_qty,
        missing_wagons: receiptData.missing_wagons,
        remarks: receiptData.remarks,
        received_at: receiptData.received_at
      };

      await rakeMovementService.receiveRakeMovement(selectedTrip.id, payload);

      toast({
        title: 'Gate-In Completed',
        description: `Successfully logged Gate-In for trip ${selectedTrip.trip_number}.`,
      });

      setDialogOpen(false);
      fetchRakeReceipts();
    } catch (err) {
      toast({
        title: 'Error',
        description: err?.message || 'Failed to complete Gate-In.',
        variant: 'destructive',
      });
    } finally {
      setSubmitting(false);
    }
  };

  if (isInitialLoad) {
    return <FullScreenLoader isLoading={isInitialLoad} />;
  }

  if (error) {
    return <div className="flex justify-center items-center h-64 text-red-500">{error}</div>;
  }

  const totalPages = Math.ceil(totalItems / pageSize) || 1;

  return (
    <div className="space-y-6">
      <div className="flex flex-col md:flex-row md:justify-between md:items-center gap-4">
        <div>
          <h1 className="text-lg font-semibold tracking-tight text-foreground">
            Rake Receipts
          </h1>
          <p className="text-xs text-muted-foreground">
            Scope, log, and audit gate-in arrivals for dispatched trains.
          </p>
        </div>

        <div className="flex gap-1 p-1 bg-secondary rounded-lg w-fit">
          <button
            onClick={() => {
              setActiveTab('pending');
              setCurrentPage(1);
            }}
            className={`px-4 py-1.5 rounded-md text-xs font-semibold transition-all ${
              activeTab === 'pending'
                ? 'bg-primary text-primary-foreground shadow-sm'
                : 'text-muted-foreground hover:text-foreground'
            }`}
          >
            Pending Gate-In
          </button>
          <button
            onClick={() => {
              setActiveTab('received');
              setCurrentPage(1);
            }}
            className={`px-4 py-1.5 rounded-md text-xs font-semibold transition-all ${
              activeTab === 'received'
                ? 'bg-primary text-primary-foreground shadow-sm'
                : 'text-muted-foreground hover:text-foreground'
            }`}
          >
            Received Receipts
          </button>
        </div>
      </div>

      <div className="mb-4 w-full mt-4">
        <div className="md:w-1/3">
          <CommonSearchInput
            placeholder="Search by trip number or RR number..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onSearch={(val) => { setSearchQuery(val); setCurrentPage(1); }}
            maxLength={50}
          />
        </div>
      </div>

      <TableBlurLoader isLoading={loading} className="mt-4">
        <div className="border border-border p-0.5 rounded-[16px] bg-card w-full transition-all transform">
          <div className="border border-border py-4 rounded-[16px] bg-background">
            {trips.length > 0 ? (
              <div>
                <RakeReceiptsTable
                  trips={trips}
                  activeTab={activeTab}
                  canDispatch={canDispatch}
                  isReadOnly={isReadOnly}
                  onOpenGateIn={handleOpenGateIn}
                  onOpenCreateDispatch={handleOpenCreateDispatch}
                />
                <PaginationComponent
                  currentPage={currentPage}
                  totalPages={totalPages}
                  totalItems={totalItems}
                  pageSize={pageSize}
                  onPageChange={setCurrentPage}
                  className="border-t"
                />
              </div>
            ) : (
              <div className="flex justify-center items-center h-64 text-muted-foreground">
                No receipts available in this category
              </div>
            )}
          </div>
        </div>
      </TableBlurLoader>

      <LogGateInDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        trip={selectedTrip}
        submitting={submitting}
        onSubmit={handleSubmitReceipt}
      />

      <CreateSecondaryDispatchDialog
        open={secondaryDispatchOpen}
        onOpenChange={setSecondaryDispatchOpen}
        parentTripId={dispatchParentTripId}
        onSuccess={() => {
          fetchRakeReceipts();
        }}
      />
    </div>
  );
};

export default RakeReceiptsPage;
